import { StripeSetupPaymentTokenResponse } from '@journeyapps-platform/types-charging-station';
import { CardElement, Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { ENV } from '../../../../../env';
import { useSystem } from '../../../../../hooks/useSystem';
import { LoaderSpinner } from '../../../../../widgets/LoaderWidget';
import { Btn, ButtonType } from '../../../../../widgets/buttons/ButtonWidget';
import { GroupedButtons } from '../../../../../widgets/buttons/GroupedButtons';
import { InputWrapperWidget } from '../../../../../widgets/inputs';
import { FormikInputWidget } from '../../../../../widgets/inputs/FormikInput';
import { FormLayoutWidget } from '../../../../../widgets/layout/FormLayoutWidget';
import { styled } from '../../../../../theme';

export interface StripeSetupCardFormProps {
  onComplete: (configuration_id: string) => any;
  backBtn: Btn;
  org_id: string;
}

export const StripeSetupCardForm: React.FC<StripeSetupCardFormProps> = (props) => {
  const [prom] = useState(() => {
    return loadStripe(ENV.STRIPE_PK);
  });
  const [complete, setComplete] = useState(false);
  const system = useSystem();

  const [customer, setCustomer] = useState<StripeSetupPaymentTokenResponse>();
  useEffect(() => {
    system.clientProvisioner
      .provisionChargingStationClient()
      .stripeGetCardSetupToken({
        org_id: system.orgStore.current_org!.id
      })
      .then((res) => {
        setCustomer(res);
      });
  }, []);

  if (!customer) {
    return (
      <S.Loading>
        <LoaderSpinner />
      </S.Loading>
    );
  }

  return (
    <Elements
      stripe={prom}
      options={{
        mode: 'setup',
        currency: 'usd',
        appearance: { theme: 'flat' },
        customerOptions: { customer: customer.customer, ephemeralKey: customer.secret }
      }}
    >
      <ElementsConsumer
        children={({ stripe, elements }) => {
          return (
            <Formik
              validate={({ name }) => {
                let errors: any = {};
                if (!name || name.trim() === '') {
                  errors['name'] = 'Required';
                }
                return errors;
              }}
              initialValues={{ name: ENV.USER_NAME }}
              onSubmit={async ({ name }) => {
                const res = await stripe?.confirmCardSetup(customer.client_secret, {
                  payment_method: {
                    card: elements?.getElement('card')!,
                    billing_details: {
                      name: name
                    }
                  }
                });
                /*
                  Could be errors such as card number already existing
                 */
                if (res?.error) {
                  system.dialogStore.errorDialog({
                    title: 'Error adding card',
                    errorMessage: res.error.message || 'Unknown error, please contact support.'
                  });
                  return;
                }

                // payment method here, is the id of the payment configuration
                props.onComplete(res!.setupIntent!.payment_method as string);
              }}
            >
              {({ submitForm }) => {
                return (
                  <FormLayoutWidget
                    sections={[
                      {
                        key: 'inputs',
                        elements: [
                          <FormikInputWidget label="Card holder" name="name" />,
                          <S.InputWrapper label="Card details" error={!complete ? 'Required' : undefined}>
                            <S.CardContainer error={!complete}>
                              <CardElement
                                onChange={({ complete }) => {
                                  setComplete(complete);
                                }}
                                options={{
                                  disableLink: true,
                                  style: {
                                    base: {
                                      color: 'white',
                                      iconColor: 'white'
                                    }
                                  }
                                }}
                              />
                            </S.CardContainer>
                          </S.InputWrapper>
                        ]
                      },
                      {
                        key: 'buttons',
                        elements: [
                          <GroupedButtons
                            btns={[
                              {
                                type: ButtonType.SECONDARY,
                                ...props.backBtn
                              },
                              {
                                type: ButtonType.PRIMARY,
                                label: 'Save and continue',
                                disabled: !complete,
                                action: async (event, loading) => {
                                  try {
                                    loading(true);
                                    await submitForm();
                                  } finally {
                                    loading(false);
                                  }
                                }
                              }
                            ]}
                          />
                        ]
                      }
                    ]}
                  />
                );
              }}
            </Formik>
          );
        }}
      />
    </Elements>
  );
};

namespace S {
  export const CardContainer = styled.div<{ error: boolean }>`
    background: ${(p) => p.theme.forms.input.background};
    border: solid 1px ${(p) => (p.error ? p.theme.forms.input.error : 'transparent')};
    padding: 12px 18px;
    border-radius: 5px;
    margin-bottom: 10px;
  `;

  export const Loading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
  `;

  export const InputWrapper = styled(InputWrapperWidget)`
    margin-top: 10px;
  `;
}
